import type { Point } from "../types";

/** Degrees from a 45° multiple within which a segment snaps to it. */
export const SOFT_ANGLE_TOLERANCE = 3;

const STEP = Math.PI / 4;

const constrain = (origin: Point, point: Point, angle: number): Point => {
  const distance = Math.hypot(point.x - origin.x, point.y - origin.y);
  return {
    x: origin.x + distance * Math.cos(angle),
    y: origin.y + distance * Math.sin(angle)
  };
};

/** Locks the segment to the nearest multiple of 45° (Shift held). */
export const constrainAngle = (origin: Point, point: Point): Point => {
  const angle =
    Math.round(Math.atan2(point.y - origin.y, point.x - origin.x) / STEP) *
    STEP;
  return constrain(origin, point, angle);
};

export interface SoftConstraint {
  point: Point;
  /** Set when the segment was close enough to a 45° multiple to snap. */
  isConstrained: boolean;
}

/** Snaps to a 45° multiple only when the segment is already almost there,
 *  so straight walls come out straight without holding Shift while an
 *  angled wall stays free. */
export const softConstrainAngle = (
  origin: Point,
  point: Point,
  toleranceDegrees = SOFT_ANGLE_TOLERANCE
): SoftConstraint => {
  const raw = Math.atan2(point.y - origin.y, point.x - origin.x);
  const nearest = Math.round(raw / STEP) * STEP;
  const offDegrees = Math.abs(((raw - nearest) * 180) / Math.PI);
  if (offDegrees > toleranceDegrees) return { point, isConstrained: false };
  return { point: constrain(origin, point, nearest), isConstrained: true };
};

/** Length (metres when calibrated, else pixels) and angle of a segment,
 *  as readout lines. Angles read clockwise from the x axis, 0..360. */
export const segmentReadout = (
  from: Point,
  to: Point,
  pixelsPerMeter: number | null | undefined
): string[] => {
  const pixels = Math.hypot(to.x - from.x, to.y - from.y);
  const length = pixelsPerMeter
    ? `${(pixels / pixelsPerMeter).toFixed(2)} m`
    : `${Math.round(pixels)} px`;
  const degrees =
    (Math.round((Math.atan2(to.y - from.y, to.x - from.x) * 180) / Math.PI) +
      360) %
    360;
  return [length, `${degrees}°`];
};
